import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import styled, { useTheme } from "styled-components";
import { denseChartHeight, truncateLabel } from "./chartLayout";
import { useContainerWidth } from "@/hooks";

interface DenseBarChartProps {
  className?: string;
  /** Row labels, drawn inside the bars. Already ordered by the caller. */
  labels: string[];
  /** Row values, index aligned with `labels`. */
  values: number[];
  /** Series name, shown in the tooltip. */
  seriesName: string;
  /** Bar color. Filled at low opacity with a matching stroke. */
  color: string;
}

/**
 * A horizontal bar chart with one row per item, for dashboard widgets whose row
 * count is driven by tenant data (users, clients) rather than by a fixed set.
 *
 * Owns the whole sizing contract so callers cannot get it half right:
 * - height comes from the row count, so rows keep a readable pitch at any count
 *   instead of being squeezed into whatever height is available
 * - width is measured, because ApexCharts only re-measures on a window resize
 *   and would otherwise ignore the dashboard's own resize handle
 * - the root is `flex: 0 0 auto` so it keeps its intrinsic height and scrolls
 *   inside `.widget-body` rather than being shrunk to fit the card
 *
 * Callers supply sorted, display ready data and nothing else.
 */
const DenseBarChartComponent = ({
  className,
  labels,
  values,
  seriesName,
  color,
}: DenseBarChartProps) => {
  const theme: any = useTheme();
  const [wrapRef, width] = useContainerWidth<HTMLDivElement>();

  const chartOptions: ApexOptions = {
    chart: {
      animations: {
        enabled: false,
      },
      stacked: false,
      background: "unset",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
        dataLabels: {
          position: "bottom", // top, center, bottom
        },
      },
    },
    theme: {
      mode: theme.name === "DARK" ? "dark" : "light",
    },
    fill: {
      colors: [color],
      type: "solid",
      opacity: 0.33,
    },
    stroke: {
      colors: [color + "90"],
      width: 1,
    },
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      style: {
        colors: [theme.palette.text.primary],
      },
      formatter: function (val, data) {
        return `${truncateLabel(labels[data.dataPointIndex])} (${val})`;
      },
    },
    grid: {
      show: false,
      padding: { top: 0, right: 0, bottom: 0, left: 0 },
    },
    // Names are drawn inside the bars, so the axis gutter would only duplicate
    // them and eat width these widgets do not have at their minimum size.
    yaxis: {
      show: false,
    },
    xaxis: {
      categories: labels,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: false,
      },
    },
    tooltip: {
      enabled: true,
      theme: theme.name === "DARK" ? "dark" : "light",
    },
    legend: {
      show: false,
    },
  };

  return (
    <div className={className} ref={wrapRef}>
      {width > 0 && (
        <Chart
          options={chartOptions}
          type="bar"
          width={width}
          height={denseChartHeight(values.length)}
          series={[
            {
              name: seriesName,
              data: values,
            },
          ]}
        />
      )}
    </div>
  );
};

const DenseBarChart = styled(DenseBarChartComponent)`
  flex: 0 0 auto;
  min-width: 0;
`;

export default DenseBarChart;
